import type { ApiQueryAbortDecisionData } from "@/cache/SpatioTemporalTileCache/ApiQueryAbortDecisionData";
import type { TileArea } from "@/cache/SpatioTemporalTileCache/TileArea";
import { TILE_SIZE } from "@/cache/SpatioTemporalTileCache/config";
import { type SceneProps, type ScenePublicApi, compositor, compositor_internal } from "@/layers/Compositor";
import type { SceneLayer } from "@/layers/LayerBase";
import type { LayerUnion, RenderableLayerUnion } from "@/layers/LayerUnion";
import type { LngLatBounds } from "@/layers/LngLatBounds";
import type { MapboxMap } from "@/layers/MapboxMap";
import type { SceneId } from "@/layers/SceneId";
import type { SceneLayerApi } from "@/layers/SceneLayerApi";
import { BackgroundMapLayerImpl } from "@/layers/background/BackgroundMapLayerImpl";
import { AviationLayerImpl } from "@/layers/geojson/AviationLayerImpl";
import { BarbsLayerImpl } from "@/layers/geojson/BarbsLayerImpl";
import { GridLayerImpl } from "@/layers/geojson/GridLayerImpl";
import { IsoBandsLayerImpl } from "@/layers/geojson/IsoBandsLayerImpl";
import { IsoLinesLayerImpl } from "@/layers/geojson/IsoLinesLayerImpl";
import { JetStreamsLayerImpl } from "@/layers/geojson/JetStreamsLayerImpl";
import { LightningLayerImpl } from "@/layers/geojson/LightningLayerImpl";
import { PressureSystemLayerImpl } from "@/layers/geojson/PressureSystemLayerImpl";
import { StationLayerImpl } from "@/layers/geojson/StationLayerImpl";
import { SymbolLayerImpl } from "@/layers/geojson/SymbolLayerImpl";
import { ThunderstormTrackingLayerImpl } from "@/layers/geojson/ThunderstormTrackingLayerImpl";
import { TropicalTropicalCycloneLayerImpl } from "@/layers/geojson/TropicalCycloneLayerImpl";
import { WeatherFrontsLayerImpl } from "@/layers/geojson/WeatherFrontsLayerImpl";
import { CustomGeoJSONLayerImpl } from "@/layers/geojson/custom";
import { DrawingLayerImpl } from "@/layers/geojson/drawing/DrawingLayerImpl";
import { EarthquakeLayerImpl } from "@/layers/geojson/earthquake/EarthquakeLayerImpl";
import { PoiLayerImpl } from "@/layers/geojson/poi/PoiLayerImpl";
import { FallbackWmsLayerImpl } from "@/layers/png";
import { BinaryAnomalyLayerImpl } from "@/layers/png/binary/BinaryAnomalyLayerImpl";
import { BinaryColormapFallbackLayerImpl } from "@/layers/png/binary/BinaryColormapFallbackLayerImpl";
import { BinaryColormapLambertLayerImpl } from "@/layers/png/binary/BinaryColormapLambertLayerImpl";
import { BinaryColormapLayerImpl } from "@/layers/png/binary/BinaryColormapLayerImpl";
import { getBinaryAnomalyRendererKind } from "@/layers/png/binary/TemperatureAnomalyPoc";
import { AviationTypes } from "@/layers/utility/aviationType";
import { LayerKindName } from "@/layers/utility/createLayerObject";
import {
  isGlobeProjection,
  isLambertProjection,
  shouldUseProjectionFallback,
  supportsMapboxCustomLayers,
} from "@/layers/utility/projectionSupport";
import { IsoLinesLayerImpl as VectorIsLinesLayerImpl } from "@/layers/vector/IsoLinesLayerImpl";
import { NwaLayerImpl } from "@/layers/vector/NwaLayerImpl";
import { WindAnimationLayerImpl } from "@/layers/wind";
import type { TabLocationMarkersLayer } from "@/map/location-markers/TabLocationMarkersLayer";
import { TabLocationMarkersLayerImpl } from "@/map/location-markers/TabLocationMarkersLayerImpl";
import reduxStore from "@/reducer";
import type { PoiLayer } from "@/reducer/client-models/PoiLayer";
import { isIos, isIpadPro } from "@/utility/platformUtils";
import { safeFmt } from "@/utility/safeFmt";
import { tilesInMapBounds } from "@/utility/tiling";
import { isColorMap } from "@/utility/typeGuard";
import type { GridDimension } from "@mm/api.meteomatics.com";
import Logger from "@mm/logging";
import {
  type AviationLayer,
  type BackgroundMapLayer,
  type BarbsLayer,
  type CartographicMapOutput,
  type CustomGeoJSONLayerOutput,
  type DrawingLayer,
  type EarthquakeLayer,
  type GridLayer,
  type GuiTimeZone,
  type IsoLinesLayer,
  type JetStreamsLayer,
  type LightningLayer,
  type MapProjection,
  type NwaLayer,
  type PressureSystemLayer,
  ProjectionNameEnum,
  type StationLayer,
  type SymbolLayer,
  type ThunderstormLayer,
  type TropicalCycloneLayer,
  type Viewport,
  ViewportKind,
  type WeatherFrontsLayer,
  type WindAnimationLayer,
  type WmsLayer,
} from "@mm/metx-workbench.meteomatics.com";
import type { FeatureCollection } from "geojson";
import { type DateTime, Duration } from "luxon";
import type { LngLat, Marker } from "mapbox-gl";
import type { IsoBandsLayer } from "./geojson/isoLayerShared";
import { WMTSLayerImpl } from "./png/WMTSLayerImpl";
import { VectorAviationLayerImpl } from "./vector/AviationLayer/VectorAviationLayerImpl";

const logger = Logger.fromFilename(__filename);

type MapboxPainterGlAdapter = {
  painter: {
    context: {
      gl: WebGL2RenderingContext;
    };
  };
};

export class Scene implements SceneLayerApi, ScenePublicApi {
  id: SceneId;
  map: MapboxMap;
  popupMarker: Marker | null = null;
  displayTime: DateTime;

  nextTime: DateTime;
  _isAnimation: boolean;
  timeOffsetMinutes: CartographicMapOutput["time_offset_mins"];
  lodBias: number;

  // TODO: use area class
  bounds: LngLatBounds;
  zoom: number;
  center: LngLat;

  // Fallback flag for layers that do not support the current projection.
  fallback = false;

  /**
   * Rendersize in hardware pixel precision
   */
  renderSize: GridDimension;
  layers: SceneLayer[] = [];
  private readonly inactiveAnomalyLayerIds = new Set<number>();

  public isActive: boolean;

  /**
   *
   * Calling this constructor will not propagate invalidations to the compositer.
   * You MUST call all invalidation functions on the compositor yourself. Since
   * This makes sense since the compositor is currently the only caller of this
   * constructor and can optimize this case.
   */
  constructor(props: SceneProps /* private compositor: Compositor | null */) {
    this.id = props.id;
    this.map = props.map;
    this.map.on("render", this.beforeRender.bind(this));

    this.bounds = this.getBoundsFromMapbox(this.map);
    this.zoom = this.map.getZoom();
    this.center = this.map.getCenter();
    this.displayTime = props.displayTime;
    this.nextTime = props.nextTime;
    this._isAnimation = props.isAnimation;
    this.timeOffsetMinutes = props.timeOffsetMinutes;
    this.renderSize = props.renderSize;
    this.lodBias = props.lodBias;

    this.isActive = true;

    // TODO Fix typing
    // @ts-ignore
    this.fallback = shouldUseProjectionFallback(this.map.getProjection().name);
  }

  signalLodBias(): void {
    compositor_internal._setZoomLevelBias(this);
  }

  getPopupMarker(): Marker | null {
    return this.popupMarker;
  }
  setPopupMarker(newMarker: Marker) {
    this.popupMarker = newMarker;
  }

  getWebGl(): WebGL2RenderingContext {
    return (this.getMapboxMap() as MapboxPainterGlAdapter).painter.context.gl;
  }

  getDateTimeWithOffset(tmpDateTime: DateTime) {
    if (this.timeOffsetMinutes) {
      return tmpDateTime.plus(Duration.fromObject({ minutes: this.timeOffsetMinutes }));
    }
    return tmpDateTime;
  }
  /**
   * @return the luxon DateTime of the displayTime with the offset
   * */
  getDisplayTimeWithOffset(): DateTime {
    return this.getDateTimeWithOffset(this.displayTime);
  }

  /**
   * @return the luxon DateTime of the displayTime "without Offset!!"
   */
  getDisplayTime(): DateTime {
    return this.displayTime;
  }
  isAnimation(): boolean {
    return this._isAnimation;
  }

  getNextTime(): DateTime {
    return this.nextTime;
  }

  getRenderSize(): GridDimension {
    return this.renderSize;
  }

  getMemoryLeaserId(): string {
    return `${this.id}`;
  }

  /**
   * Called each time before the map is redrawn.
   */
  beforeRender() {
    if (!compositor.inUse(this.id) || !this.isActive) {
      // Skip rendering if the map is unused / in the process of deallocation.
      return;
    }
    // the actual rendering of the layers is then performed by mapbox internally along the lines of
    for (const layer of this.layers) {
      const layerProps = layer.getLayerProps();
      if (layerProps.show) {
        layer.beforeRender();
      }
    }
  }

  repaint() {
    compositor_internal._repaint(this);
  }

  deallocate() {
    this.isActive = false;
    for (const layer of this.layers) {
      layer.removeLayer();
    }
  }

  getReferenceTileSize() {
    return TILE_SIZE;
  }

  // TODO: this conflicts with untiled mapbox layers
  // TODO: cache this?
  getMapViewport(_zLodBias?: number): { zoom: number; zoomWithPrecision: number; viewportArea: TileArea } {
    const zLodBias = _zLodBias ? _zLodBias : this.getLodBias();
    return tilesInMapBounds(this.map, this.getReferenceTileSize(), zLodBias);
  }

  /**
   * Get information about the active data subspaces that is required to decide whether a tile request
   * should be aborted.
   */
  // TODO: either add a `getActiveDataSubspaces()` for other tasks like data prefetching in animations,
  // or generalize this getter to cover both cases: prefetching and abort decision making
  getAbortDecisionDataSet(): ApiQueryAbortDecisionData[] {
    const areas: Record<number, TileArea> = {};

    return this.layers.flatMap((layer) => {
      const parameters = layer.getActiveWeatherParametersAsString();
      const appliedStyle = layer.getAppliedStyle();
      const appliedEnsSelect = layer.getAppliedEnsSelect();

      if (parameters.length === null) {
        return [];
      }
      const zoomLevelBias = this.getLodBias();

      if (areas[zoomLevelBias] == null) {
        areas[zoomLevelBias] = this.getMapViewport().viewportArea;
      }

      const area = areas[zoomLevelBias];

      return [
        {
          area,
          // TODO: so, this transfers a lot per parameter because of the readonly reference to the parameter structure.
          //       The same is true for the whole ApiQuery per tile
          parameters,
          appliedStyle,
          appliedEnsSelect,
        },
      ];
    });
  }

  setDisplayTime(displayTime: DateTime) {
    this.displayTime = displayTime;
    compositor_internal._setDisplayTimeAndTemporalDiscretization(this);
  }
  setIsAnimation(isAnimation: boolean) {
    this._isAnimation = isAnimation;
  }

  setNextTime(nextTime: DateTime) {
    this.nextTime = nextTime;
    compositor_internal._setDisplayTimeAndTemporalDiscretization(this);
  }
  setTimeOffsetMinutes(durationIsoStr: CartographicMapOutput["time_offset_mins"] | undefined) {
    this.timeOffsetMinutes = durationIsoStr || 0;
    compositor_internal._setDisplayTimeAndTemporalDiscretization(this); // check
  }
  setProjection(mapProjection: MapProjection) {
    //TODO: Fix type is missing
    // @ts-ignore
    this.map.setProjection(mapProjection);
    if (mapProjection.name === ProjectionNameEnum.globe) {
      this.map.setFog({});
    }
  }

  getProjection(): MapProjection {
    //TODO: Fix type is missing
    // @ts-ignore
    return this.map.getProjection();
  }
  getLodBias(): number {
    return this.lodBias;
  }

  setLodBias(lodBias: number) {
    this.lodBias = lodBias;
    this.signalLodBias();
  }

  setLayerStackDescription(
    layers: RenderableLayerUnion[],
    timezone: GuiTimeZone,
    fallback?: boolean,
    wmtsEnabled?: boolean,
  ) {
    const changed = this.reconciler(layers, timezone, fallback, wmtsEnabled);
    if (changed) {
      // start this from behind
      for (let idx = this.layers.length - 1; idx >= 0; idx--) {
        const currentLayer = this.layers[idx];
        const nextLayer = this.layers[idx + 1];
        currentLayer.setTimezone(timezone);
        currentLayer.moveZIndex(nextLayer ? nextLayer.mapboxIndex : undefined);
      }

      compositor_internal._setLayerStack(this);
    }
  }

  setViewportDescription(viewport: Viewport) {
    // TODO: setGridDimensions? This should work without react driving the update loop
    this.syncMapboxToViewport(viewport);
    this.bounds = this.getBoundsFromMapbox(this.map);
    compositor_internal._setBounds(this);
  }

  getMapboxMap() {
    return this.map;
  }

  isMapboxLoaded() {
    return this.getMapboxMap()._loaded ?? false;
  }

  /**
   * Updates the rendering engine to match the viewport state in mapbox.
   */
  syncToMapboxViewport() {
    this.bounds = this.getBoundsFromMapbox(this.map);
    this.center = this.map.getCenter();
    this.zoom = this.map.getZoom();
    compositor_internal._setBounds(this); // this triggers a repaint of the scene as every other _function does.

    // TODO: we implicitly expect canvas resizes to always implicitly update the map bounds
    // and implicitly update rendersize here
    this.renderSize = this.getRenderSizeFromMapbox();
    compositor_internal._setRenderSize(this);
  }

  /**
   * Updates the viewport of mapbox to match the viewport of the rendering engine.
   */
  syncMapboxToViewport(viewport: Viewport): void {
    const map = this.getMapboxMap();

    switch (viewport.kind) {
      // This is temporary fix, while we figure out better approach on syncing viewport
      // Globe projection requires setting center and zoom too, but search is requiring
      // only setting bounds, so another ViewportKind is required
      case ViewportKind.ViewportFull:
        map.fitBounds(
          [
            { lat: viewport.southWest_lat, lng: viewport.southWest_lng },
            { lat: viewport.northEast_lat, lng: viewport.northEast_lng },
          ],
          {
            animate: false,
          },
        );

        map.setCenter([viewport.center_lng, viewport.center_lat]);
        map.setZoom(viewport.zoom);

        break;
      case ViewportKind.ViewportContainBounds:
        map.fitBounds(
          [
            { lat: viewport.southWest_lat, lng: viewport.southWest_lng },
            { lat: viewport.northEast_lat, lng: viewport.northEast_lng },
          ],
          {
            animate: false,
          },
        );

        // We need to set center for lamber projection, otherwise it breaks
        map.setCenter([viewport.center_lng, viewport.center_lat]);

        break;
      case ViewportKind.ViewportCenterPoint:
        map.setCenter([viewport.center_lng, viewport.center_lat]);
        map.setZoom(viewport.zoom);

        break;
    }
  }
  protected decodeLayer(
    layer: RenderableLayerUnion,
    timezone: GuiTimeZone,
    fallback?: boolean,
    wmtsEnabled?: boolean,
  ): SceneLayer | null {
    // TODO: the backend generates incorrect typings for `layer.kind`
    switch (layer.kind) {
      case LayerKindName.WmsLayerDescription: {
        const wmsLayer = layer as WmsLayer;
        const renderer = this.getWmsRendererKindFromLayer(wmsLayer, fallback, wmtsEnabled);
        switch (renderer) {
          case "binary_anomaly":
            this.inactiveAnomalyLayerIds.delete(layer.id);
            logger.log(`Routing layer ${layer.id} through the binary anomaly renderer.`);
            return new BinaryAnomalyLayerImpl(layer.id, wmsLayer, this, timezone);
          case "unsupported_anomaly":
            if (!this.inactiveAnomalyLayerIds.has(layer.id)) {
              logger.warn(`Anomaly layer ${layer.id} is inactive because the map projection is not Mercator.`);
              this.inactiveAnomalyLayerIds.add(layer.id);
            }
            return null;
          case "binary":
            return new BinaryColormapLayerImpl(layer.id, wmsLayer, this, timezone);
          case "binary_fallback":
            return new BinaryColormapFallbackLayerImpl(layer.id, wmsLayer, this, timezone);
          case "binary_lambert_raster":
            return new BinaryColormapLambertLayerImpl(layer.id, wmsLayer, this, timezone);
          case "wmts":
            return new WMTSLayerImpl(layer.id, wmsLayer, this, timezone);
          case "raster":
            return new FallbackWmsLayerImpl(layer.id, wmsLayer, this, timezone);
          default:
            return new FallbackWmsLayerImpl(layer.id, wmsLayer, this, timezone);
        }
      }

      case LayerKindName.GridLayerDescription:
        return new GridLayerImpl(layer.id, layer as GridLayer, this, timezone);
      case LayerKindName.IsoLinesLayerDescription:
        if (layer.experimental) {
          return new VectorIsLinesLayerImpl(layer.id, layer as IsoLinesLayer, this, timezone);
        }
        return new IsoLinesLayerImpl(layer.id, layer as IsoLinesLayer, this, timezone);
      case LayerKindName.IsoBandsLayerDescription:
        return new IsoBandsLayerImpl(layer.id, layer as IsoBandsLayer, this, timezone);
      case LayerKindName.BarbsLayerDescription:
        return new BarbsLayerImpl(layer.id, layer as BarbsLayer, this, timezone);
      case LayerKindName.SymbolLayerDescription:
        return new SymbolLayerImpl(layer.id, layer as SymbolLayer, this, timezone);
      case LayerKindName.BackgroundMapDescription:
        return new BackgroundMapLayerImpl(layer.id, layer as BackgroundMapLayer, this, timezone);
      case LayerKindName.StationLayerDescription:
        return new StationLayerImpl(layer.id, layer as StationLayer, this, timezone);
      case LayerKindName.GenericPoiLayerDescription:
        return new PoiLayerImpl(layer.id, layer as PoiLayer, this, timezone);
      case LayerKindName.AviationLayerDescription: {
        const aviationLayer = layer as AviationLayer;
        if (
          aviationLayer.aviation_type === AviationTypes.metar ||
          aviationLayer.aviation_type === AviationTypes.taf ||
          aviationLayer.aviation_type === AviationTypes.isigmet
        ) {
          return new VectorAviationLayerImpl(layer.id, layer as AviationLayer, this, timezone);
        }
        // Fallback to GeoJson layer if it's not a supported aviation layer
        return new AviationLayerImpl(layer.id, layer as AviationLayer, this, timezone);
      }
      case LayerKindName.PressureSystemLayerDescription:
        return new PressureSystemLayerImpl(layer.id, layer as PressureSystemLayer, this, timezone);
      case LayerKindName.LightningLayerDescription:
        return new LightningLayerImpl(layer.id, layer as LightningLayer, this, timezone);
      case LayerKindName.TropicalCycloneLayerDescription:
        return new TropicalTropicalCycloneLayerImpl(layer.id, layer as TropicalCycloneLayer, this, timezone);
      case LayerKindName.WeatherFrontsLayerDescription:
        return new WeatherFrontsLayerImpl(layer.id, layer as WeatherFrontsLayer, this, timezone);
      case LayerKindName.JetStreamsLayerDescription:
        return new JetStreamsLayerImpl(layer.id, layer as JetStreamsLayer, this, timezone);
      case LayerKindName.EarthquakeLayerDescription:
        return new EarthquakeLayerImpl(layer.id, layer as EarthquakeLayer, this, timezone);
      case LayerKindName.ThunderstormTrackingLayerDescription:
        return new ThunderstormTrackingLayerImpl(layer.id, layer as ThunderstormLayer, this, timezone);
      case LayerKindName.WindAnimationLayerDescription:
        return new WindAnimationLayerImpl(layer.id, layer as WindAnimationLayer, this, timezone);
      case LayerKindName.CustomGeoJSONLayerDescription:
        return new CustomGeoJSONLayerImpl(layer.id, layer as CustomGeoJSONLayerOutput, this, timezone);
      case LayerKindName.NwaLayerDescription: {
        return new NwaLayerImpl(layer.id, layer as NwaLayer, this, timezone);
      }
      case LayerKindName.DrawingLayerDescription: {
        const drawings = // @ts-ignore Messed up typing
          reduxStore.getState().tabGroup.present.maps[layer.id_cartographicmap].drawing as FeatureCollection;
        return new DrawingLayerImpl(layer.id, layer as DrawingLayer, this, timezone, drawings);
      }
      case LayerKindName.TabLocationMarkersLayerDescription:
        return new TabLocationMarkersLayerImpl(layer.id, layer as TabLocationMarkersLayer, this, timezone);

      default:
        logger.error(safeFmt`unknown layer kind <${layer.kind ?? "(no kind field)"}>`);
        return null;
    }
  }

  private isFallbackChanged(fallback: boolean, layer: SceneLayer) {
    return fallback !== this.fallback && layer.getLayerProps().kind === "WmsLayerDescription";
  }

  private getWmsRendererKindFromInstance(layer: SceneLayer) {
    if (layer instanceof BinaryAnomalyLayerImpl) {
      return "binary_anomaly";
    }
    if (layer instanceof BinaryColormapFallbackLayerImpl) {
      return "binary_fallback";
    }
    if (layer instanceof BinaryColormapLambertLayerImpl) {
      return "binary_lambert_raster";
    }
    if (layer instanceof BinaryColormapLayerImpl) {
      return "binary";
    }
    if (layer instanceof WMTSLayerImpl) {
      return "wmts";
    }
    if (layer instanceof FallbackWmsLayerImpl) {
      return "raster";
    }
    return "raster";
  }

  private getWmsRendererKindFromLayer(layer: WmsLayer, fallback?: boolean, wmtsEnabled?: boolean) {
    const anomalyRenderer = getBinaryAnomalyRendererKind(layer, this.map.getProjection()?.name);
    if (anomalyRenderer) {
      return anomalyRenderer;
    }
    const binaryRenderer = this.getPreferredBinaryRendererKind(layer);
    if (binaryRenderer) {
      return binaryRenderer;
    }
    if (fallback || isIos || isIpadPro) {
      return "raster";
    }
    if (layer.model === "mix" && wmtsEnabled) {
      return "wmts";
    }
    return "raster";
  }

  private shouldUseAnyBinaryColormap(layer: WmsLayer, _fallback?: boolean): boolean {
    return this.getPreferredBinaryRendererKind(layer) != null;
  }

  private getPreferredBinaryRendererKind(
    layer: WmsLayer,
  ): "binary" | "binary_fallback" | "binary_lambert_raster" | undefined {
    if (isIos || isIpadPro || !layer.color_map) {
      return undefined;
    }
    if (isColorMap(layer.color_map)) {
      return undefined;
    }

    const projectionName = this.map.getProjection()?.name;
    if (isGlobeProjection(projectionName)) {
      return "binary_fallback";
    }

    // Lambert + custom colormap uses a dedicated rasterized binary path:
    // - Fetch binary scalar tiles.
    // - Apply custom LUT on CPU.
    // - Feed the result into a Mapbox raster layer so Lambert clipping/masking is handled by Mapbox.
    //
    // Tradeoff: better Lambert edge behavior and placement, but more CPU work than the pure GPU custom-layer path.
    if (isLambertProjection(projectionName)) {
      return "binary_lambert_raster";
    }

    if (supportsMapboxCustomLayers(projectionName)) {
      return "binary";
    }
    return undefined;
  }

  /**
   * Reconciler that updates the rendering engine to match the given layer stack with
   * the minimal amount of invalidations.
   *
   * If the type of a layer changes, a new `id` MUST be given to the layer.
   *
   * @return boolean indicating if anything changed. `false` if nothing changed, `true`
   * for any modification. For example: layer was added, removed, changed or if the order of layers changed.
   */
  private reconciler(
    descriptionLayers: RenderableLayerUnion[],
    timezone: GuiTimeZone,
    fallback?: boolean,
    wmtsEnabled?: boolean,
  ) {
    // [STEP 1 -- remove no longer used layers] relative complement currentState / prevState
    const curr: (null | RenderableLayerUnion)[] = [...descriptionLayers]; // TODO: is a copy needed?
    const prev = this.layers;
    const res: SceneLayer[] = [];

    // indicates that the transfer function changed and that we have to repaint
    // the layer, even when the data inputs did not change.
    let mustRerender = false;

    for (const [pI, p] of prev.entries()) {
      const cI = curr.findIndex((c) => c && c.id === p.uid);

      // Trigger removing layer, if aviation type changes
      // TODO Remove this once we will have support for all
      //  aviation layers on vector tiles
      const hasAviationTypeChanges =
        // @ts-ignore
        p.getLayerProps().aviation_type !== curr[cI]?.aviation_type;

      // TODO: Remove this once WMTS layer will support all models
      const hasWmtsTransition =
        p.getLayerProps().kind === LayerKindName.WmsLayerDescription &&
        wmtsEnabled && // Only care about model when WMTS is enabled
        cI >= 0 &&
        curr[cI] &&
        // Check if transitioning to/from 'mix' model
        ((p.getLayerProps() as WmsLayer).model === "mix") !== ((curr[cI] as WmsLayer).model === "mix");

      const hasBinaryTransition =
        p.getLayerProps().kind === LayerKindName.WmsLayerDescription &&
        cI >= 0 &&
        curr[cI] &&
        this.shouldUseAnyBinaryColormap(curr[cI] as WmsLayer, fallback) !==
          (p instanceof BinaryColormapLayerImpl ||
            p instanceof BinaryColormapFallbackLayerImpl ||
            p instanceof BinaryColormapLambertLayerImpl);

      const hasWmsRendererTransition =
        p.getLayerProps().kind === LayerKindName.WmsLayerDescription &&
        cI >= 0 &&
        curr[cI] &&
        this.getWmsRendererKindFromLayer(curr[cI] as WmsLayer, fallback, wmtsEnabled) !==
          this.getWmsRendererKindFromInstance(p);

      const hasTimezoneChanges = p.timezone !== timezone;

      // Hack to prevent layer recreation on any of changes in state
      // TODO Remove this as soon as drawing layer will get extracted
      // as actual layer, with actual id
      const isDrawingLayer = p.getLayerProps().kind === LayerKindName.DrawingLayerDescription;

      // if fallback state changes, make sure we remove previous WMS layer,
      // so reconciler can create new layer in place
      if (
        isDrawingLayer ||
        (cI >= 0 &&
          !this.isFallbackChanged(!!fallback, p) &&
          !hasAviationTypeChanges &&
          !hasWmtsTransition &&
          !hasBinaryTransition &&
          !hasWmsRendererTransition)
      ) {
        // biome-ignore lint/style/noNonNullAssertion: TODO a lot of todos around
        const cI_mustRerender = !isDrawingLayer ? p.checkChanges(curr[cI]!) : false;

        mustRerender = mustRerender || cI_mustRerender || hasTimezoneChanges;
        res[cI] = p;
        curr[cI] = null;
        if (cI !== pI) {
          // if the layer was moved, make sure we rerender even if the layer
          // itself did not change internally.
          mustRerender = true;
        }
      } else {
        // allow the layer to perform explicit deallocations
        p.releaseLayer?.();
        p.removeLayer();
        mustRerender = true;
      }
    }

    // Update compositor fallback state
    this.fallback = !!fallback;

    // [STEP 2 -- add new layers] relative complement prevState / currentState
    for (const [cI, c] of curr.entries()) {
      if (c != null) {
        const layer = this.decodeLayer(c, timezone, fallback, wmtsEnabled);
        if (layer != null) {
          res[cI] = layer;
          mustRerender = true;
        }
      }
    }

    this.layers = res;
    return mustRerender;
  }

  /**
   * Get Rendersize in hardware pixel units.
   */
  private getRenderSizeFromMapbox(): GridDimension {
    const canvas = this.map.getCanvas();
    const width = Math.round(canvas.width);
    const height = Math.round(canvas.height);

    return { width, height };
  }

  /**
   * Get the bounds from Mapbox.
   */
  private getBoundsFromMapbox(map: MapboxMap = this.getMapboxMap()): LngLatBounds {
    // TODO: use `Area` here?
    const bounds = map.getBounds();
    const center = map.getCenter();
    const sw = bounds ? bounds.getSouthWest() : center;
    const ne = bounds ? bounds.getNorthEast() : center;

    return {
      southWest: {
        lng: sw.lng,
        lat: sw.lat,
      },
      northEast: {
        lng: ne.lng,
        lat: ne.lat,
      },
    };
  }
}
